import { baseRequest } from '@/utils/api';
import axios from 'axios';
import { createBaseAPIConfig } from '@/config/api';
import { handleError, getToken } from '@/utils/request';
import {
  CONNECTION_CONNECTING,
  CONNECTION_DISCONNECTED,
  CONNECTION_ERROR,
  connectTaskEvents,
} from './aiConnection';

// 与 views/teacher/ai/AiTaskWorkbench.vue、views/student/TeachingAssistant.vue 保持一致
const TERMINAL_STATUSES = ['SUCCEEDED', 'FAILED', 'STOPPED', 'PARTIAL_SUCCESS'];

// baseRequest 仅提供 get/post；后端重命名/删除使用 PATCH/DELETE，
// 这里用同一 base 配置 + token 逻辑补一个最小 PATCH/DELETE 请求器。
const baseAPI = axios.create(createBaseAPIConfig());

async function baseRequestMethod(method, url, data = {}) {
  try {
    const token = getToken();
    const response = await baseAPI[method](url, data, {
      headers: token ? { token } : {},
    });
    return response.data;
  } catch (error) {
    const message = handleError(error);
    throw new Error(message);
  }
}

function readStorage(key) {
  try {
    return JSON.parse(localStorage.getItem('lingxi:ai:' + key));
  } catch (e) {
    return null;
  }
}

function stateConversations(rootState) {
  return (rootState.ai && rootState.ai.conversations) || [];
}

export default {
  // 从 localStorage 恢复会话/任务，并重新拉取快照与消息
  async restore({ commit, dispatch }) {
    const conversationId = readStorage('currentConversationId');
    const taskId = readStorage('currentTaskId');
    commit('RESTORE', { conversationId, taskId });
    if (conversationId) {
      await dispatch('fetchMessages', { conversationId, currentPage: 1, pageSize: 50 });
    }
    if (taskId) {
      try {
        await dispatch('fetchTask', taskId);
      } catch (e) {
        // 快照拉取失败（网络/任务不存在）：仍尝试建立 SSE 订阅，
        // 由连接层判定任务是否可恢复；若任务不存在则标记失败而非静默死路。
        try {
          await dispatch('connectTask', taskId);
        } catch (connError) {
          commit('SET_TASK_ERROR', {
            taskId,
            message: '任务无法恢复：' + ((connError && connError.message) || '连接失败'),
          });
        }
      }
    }
    return { conversationId, taskId };
  },

  // 创建任务（持久化任务接口）
  async createTask({ commit, dispatch }, { query, taskType, inputs, conversationId }) {
    commit('SET_LOADING', { key: 'createTask', value: true });
    commit('CLEAR_ERROR');
    try {
      const res = await baseRequest.post('/api/ai/tasks', {
        query,
        taskType,
        inputs,
        conversationId,
      });
      const data = res && res.data ? res.data : res;
      if (data && data.taskId) {
        commit('SET_CURRENT_TASK', data.taskId);
        if (data.conversationId) {
          commit('SET_CURRENT_CONVERSATION', data.conversationId);
        } else if (!conversationId) {
          // 新任务未归属任何会话：清空当前会话，避免沿用上一会话 id 造成消息串流
          commit('SET_CURRENT_CONVERSATION', null);
        }
        await dispatch('fetchTask', data.taskId);
      }
      return data;
    } catch (e) {
      commit('SET_ERROR', e.message || '创建任务失败');
      throw e;
    } finally {
      commit('SET_LOADING', { key: 'createTask', value: false });
    }
  },

  // 拉取任务快照
  async fetchTask({ commit }, taskId) {
    commit('SET_LOADING', { key: 'task', value: true });
    commit('CLEAR_ERROR');
    try {
      const res = await baseRequest.get(`/api/ai/tasks/${taskId}`);
      const snapshot = res && res.data ? res.data : res;
      commit('SET_TASK', snapshot);
      return snapshot;
    } catch (e) {
      commit('SET_ERROR', e.message || '获取任务失败');
      throw e;
    } finally {
      commit('SET_LOADING', { key: 'task', value: false });
    }
  },

  // 停止任务
  async stopTask({ commit }, taskId) {
    commit('CLEAR_ERROR');
    try {
      const res = await baseRequest.post(`/api/ai/tasks/${taskId}/stop`);
      const snapshot = res && res.data ? res.data : res;
      commit('SET_TASK', snapshot);
      return snapshot;
    } catch (e) {
      commit('SET_ERROR', e.message || '停止任务失败');
      throw e;
    }
  },

  // 重试子任务
  async retrySubtask({ commit, dispatch }, { taskId, subtaskId }) {
    commit('CLEAR_ERROR');
    commit('SET_LOADING', { key: 'retrySubtask', value: true });
    try {
      const res = await baseRequest.post(
        `/api/ai/tasks/${taskId}/subtasks/${subtaskId}/retry`
      );
      const snapshot = res && res.data ? res.data : res;
      commit('SET_TASK', snapshot);
      // 仅活动态重建 SSE 订阅：终态任务不会再产生事件，订阅只会留下死监听器
      if (snapshot && snapshot.taskId && !TERMINAL_STATUSES.includes(snapshot.status)) {
        await dispatch('connectTask', snapshot.taskId);
      }
      return snapshot;
    } catch (e) {
      commit('SET_ERROR', e.message || '重试子任务失败');
      throw e;
    } finally {
      commit('SET_LOADING', { key: 'retrySubtask', value: false });
    }
  },

  // 会话列表（分页：currentPage + pageSize）
  async fetchConversations({ commit }, { currentPage = 1, pageSize = 20 } = {}) {
    commit('SET_LOADING', { key: 'conversations', value: true });
    commit('CLEAR_ERROR');
    try {
      const res = await baseRequest.get('/api/ai/conversations', { currentPage, pageSize });
      const page = res && res.data ? res.data : res;
      commit('SET_CONVERSATIONS', {
        list: page.list,
        total: page.total,
        currentPage: page.currentPage,
        pageSize: page.pageSize,
      });
      return page;
    } catch (e) {
      commit('SET_ERROR', e.message || '获取会话列表失败');
      throw e;
    } finally {
      commit('SET_LOADING', { key: 'conversations', value: false });
    }
  },

  // 会话消息历史（分页：currentPage + pageSize）
  async fetchMessages({ commit }, { conversationId, currentPage = 1, pageSize = 20 }) {
    commit('SET_LOADING', { key: 'messages', value: true });
    commit('CLEAR_ERROR');
    try {
      const res = await baseRequest.get(
        `/api/ai/conversations/${conversationId}/messages`,
        { currentPage, pageSize }
      );
      const page = res && res.data ? res.data : res;
      commit('SET_MESSAGES', {
        conversationId,
        list: page.list,
        total: page.total,
      });
      return page;
    } catch (e) {
      commit('SET_ERROR', e.message || '获取消息失败');
      throw e;
    } finally {
      commit('SET_LOADING', { key: 'messages', value: false });
    }
  },

  // 重命名会话
  async renameConversation({ commit }, { conversationId, title }) {
    commit('CLEAR_ERROR');
    try {
      await baseRequestMethod('patch', `/api/ai/conversations/${conversationId}`, { title });
      const list = stateConversations(this.state);
      const updated = list.map((c) =>
        c.conversationId === conversationId ? { ...c, title } : c
      );
      commit('SET_CONVERSATIONS', {
        list: updated,
        total: this.state.ai.conversationsTotal,
        currentPage: this.state.ai.conversationsPage,
        pageSize: this.state.ai.conversationsPageSize,
      });
    } catch (e) {
      commit('SET_ERROR', e.message || '重命名会话失败');
      throw e;
    }
  },

  // 删除会话
  async deleteConversation({ commit, state }, conversationId) {
    commit('CLEAR_ERROR');
    try {
      await baseRequestMethod('delete', `/api/ai/conversations/${conversationId}`);
      const list = state.conversations.filter((c) => c.conversationId !== conversationId);
      commit('SET_CONVERSATIONS', {
        list,
        total: Math.max(0, state.conversationsTotal - 1),
        currentPage: state.conversationsPage,
        pageSize: state.conversationsPageSize,
      });
      if (state.currentConversationId === conversationId) {
        commit('SET_CURRENT_CONVERSATION', null);
      }
    } catch (e) {
      commit('SET_ERROR', e.message || '删除会话失败');
      throw e;
    }
  },

  // 选择当前会话
  selectConversation({ commit }, conversationId) {
    commit('SET_CURRENT_CONVERSATION', conversationId);
  },

  // 选择当前任务
  selectTask({ commit }, taskId) {
    commit('SET_CURRENT_TASK', taskId);
  },

  // ---- SSE 对接接口（复用 @/utils/sse 官方实现，见 ./aiConnection.js） ----
  // 建立任务事件订阅连接；返回一个可中断的控制器。
  connectTask({ commit }, taskId) {
    commit('SET_CONNECTION', { taskId, status: CONNECTION_CONNECTING });
    return connectTaskEvents(taskId, {
      onEvent: (event) => {
        commit('APPEND_EVENT', { taskId, event });
        // 收到任何事件即视为连接活跃，清除历史错误提示
        commit('CLEAR_ERROR');
        if (event.type === 'task_error') {
          const message =
            (event.payload && event.payload.message) || 'AI 任务执行失败';
          commit('SET_TASK_ERROR', { taskId, message });
        }
        if (event.payload && event.payload.snapshot) {
          commit('SET_TASK', event.payload.snapshot);
        }
      },
      onStatus: (status, error) => {
        commit('SET_CONNECTION', { taskId, status, error });
        // 任务不存在（HTTP 404）：连接不可恢复，标记任务失败而非静默死路
        if (status === CONNECTION_ERROR && error && error.status === 404) {
          commit('SET_TASK_ERROR', {
            taskId,
            message: '任务不存在或已被删除，无法恢复连接',
          });
        }
      },
    });
  },

  // 断开任务事件订阅
  disconnectTask({ commit }, taskId) {
    commit('SET_CONNECTION', { taskId, status: CONNECTION_DISCONNECTED });
  },

  // 重置 AI 状态
  reset({ commit }) {
    commit('RESET');
  },
};
